import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  Store,
  Package,
  Wallet,
  Clock,
  TrendingUp,
  ArrowUpRight,
  AlertCircle,
  Shield,
} from "lucide-react";
import { toast } from "sonner";

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: getAuthHeaders(),
      });
      setDashboard(res.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </AdminLayout>
    );
  }

  const stats = [
    {
      label: "Total Users",
      value: dashboard?.total_users || 0,
      icon: Users,
      color: "blue",
      link: "/admin/users",
    },
    {
      label: "Total Sellers",
      value: dashboard?.total_sellers || 0,
      icon: Store,
      color: "purple",
      link: "/admin/sellers",
    },
    {
      label: "Pending Sellers",
      value: dashboard?.pending_sellers || 0,
      icon: Clock,
      color: "yellow",
      link: "/admin/sellers",
      alert: dashboard?.pending_sellers > 0,
    },
    {
      label: "Total Products",
      value: dashboard?.total_products || 0,
      icon: Package,
      color: "green",
      link: "/admin/products",
    },
    {
      label: "Pending Products",
      value: dashboard?.pending_products || 0,
      icon: Package,
      color: "orange",
      link: "/admin/products",
      alert: dashboard?.pending_products > 0,
    },
    {
      label: "Pending Withdrawals",
      value: dashboard?.pending_withdrawals || 0,
      icon: Wallet,
      color: "red",
      link: "/admin/withdrawals",
      alert: dashboard?.pending_withdrawals > 0,
    },
    {
      label: "Pending KYC",
      value: dashboard?.pending_kyc || 0,
      icon: Shield,
      color: "indigo",
      link: "/admin/users",
      alert: dashboard?.pending_kyc > 0,
    },
  ];

  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    yellow: "bg-yellow-50 text-yellow-600",
    green: "bg-green-50 text-green-600",
    orange: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    indigo: "bg-indigo-50 text-indigo-600",
  };

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, idx) => (
          <Link key={idx} to={stat.link}>
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                      {stat.label}
                      {stat.alert && (
                        <AlertCircle className="w-3 h-3 text-red-500" />
                      )}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 font-heading">
                      {stat.value}
                    </p>
                  </div>
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      colorClasses[stat.color]
                    }`}
                  >
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Pending Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboard?.pending_sellers > 0 && (
              <Link to="/admin/sellers">
                <Button variant="outline" className="w-full justify-between text-yellow-700 border-yellow-200 bg-yellow-50">
                  <span className="flex items-center gap-2">
                    <Store className="w-4 h-4" />
                    {dashboard.pending_sellers} Sellers awaiting approval
                  </span>
                  <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
            {dashboard?.pending_products > 0 && (
              <Link to="/admin/products">
                <Button variant="outline" className="w-full justify-between text-amber-700 border-amber-200 bg-amber-50">
                  <span className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {dashboard.pending_products} Products pending review
                  </span>
                  <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
            {dashboard?.pending_withdrawals > 0 && (
              <Link to="/admin/withdrawals">
                <Button variant="outline" className="w-full justify-between text-red-700 border-red-200 bg-red-50">
                  <span className="flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    {dashboard.pending_withdrawals} Withdrawal requests
                  </span>
                  <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
            {dashboard?.pending_kyc > 0 && (
              <Link to="/admin/users">
                <Button variant="outline" className="w-full justify-between text-indigo-700 border-indigo-200 bg-indigo-50">
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {dashboard.pending_kyc} KYC verifications pending
                  </span>
                  <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
            {!dashboard?.pending_sellers && !dashboard?.pending_products && !dashboard?.pending_withdrawals && !dashboard?.pending_kyc && (
              <div className="text-center py-8 text-gray-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>All caught up! No pending actions.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Quick Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/products">
              <Button className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 justify-between">
                <span className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Add Affiliate Product
                </span>
                <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/admin/content">
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  Manage Content
                </span>
                <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/admin/mlm">
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  MLM Commission Settings
                </span>
                <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
